import { InputAdornment, InputLabel } from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import OutlinedInput from '@mui/material/OutlinedInput';
import Select, { SelectChangeEvent, SelectProps } from '@mui/material/Select';
import { useTheme } from '@mui/material/styles';
import * as React from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';
import { FieldErrorTooltip } from '../FieldErrorTooltip/FieldErrorTooltip';

export interface ComboSelectProps<T = any> extends SelectProps {
    options: Array<T & { label: string; value: string }>; // These are the options to select from.
    selectedValue: string | string[]; // this will be the pre selected value. if string, then it is a single select, if array, then it is a multi select
    // eslint-disable-next-line no-unused-vars
    onSelectChange(value: string | string[]): void; // this will be the callback function to get the selected value/s
    icon?: React.ElementType;
    multiple?: boolean;
    preText?: string;
    MenuProps?: any; // Add MenuProps to allow custom dropdown styling
    // eslint-disable-next-line
    renderValue?: (value: any) => React.ReactNode;
    // eslint-disable-next-line no-unused-vars
    renderMenuItem?: (option: T & { value: string; label: string }, index?: number) => React.ReactNode; // Custom menu item content renderer
    listItemStyle?: React.CSSProperties;
    /** When true, an error state renders a red ErrorIcon adornment whose tooltip shows `errorText`. */
    showTooltipError?: boolean;
    /** Force the error tooltip open (e.g. when the row has a single validation error). */
    openTooltipError?: boolean;
    /** Message shown inside the error tooltip. */
    errorText?: React.ReactNode;
    /** Overrides the empty-state placeholder style (default is the blue link colour, italic). */
    placeholderStyle?: React.CSSProperties;
}

export const ComboSelect = <T = any,>(props: ComboSelectProps<T>) => {
    const {
        options,
        multiple,
        placeholder,
        label,
        icon = ExpandMoreIcon,
        hidden,
        disabled,
        selectedValue,
        onSelectChange,
        size = 'medium',
        onBlur,
        preText,
        error,
        sx,
        MenuProps,
        renderValue,
        renderMenuItem,
        listItemStyle,
        showTooltipError = false,
        openTooltipError = false,
        errorText,
        placeholderStyle
    } = props;
    const theme = useTheme();
    const { t } = useTranslation();
    const [selectedData, setSelectedData] = React.useState<string[] | string>(selectedValue || '');

    React.useEffect(() => {
        setSelectedData(selectedValue || '');
    }, [selectedValue]);

    const handleChange = (event: SelectChangeEvent<typeof selectedData>) => {
        const {
            target: { value }
        } = event;
        setSelectedData(
            value
            // On autofill we get a stringified value.
            // typeof value === 'string' ? value.split(',') : value
        );
        onSelectChange?.(value);
    };

    // Match the AdminFilters `Option` dropdown item styling (font, size, color, background, states).
    const menuItemSx = {
        ...(theme.typography as any).labels.large,
        backgroundColor: theme.palette.semantic.bg.default,
        color: theme.palette.semantic.int.selected,
        padding: '6px 10px',
        display: 'flex',
        alignItems: 'center',
        fontWeight: '400 !important',
        '&:hover': { backgroundColor: theme.palette.neutral[100] },
        '&.Mui-selected': { backgroundColor: theme.palette.semantic.bg.brandLight1 },
        '&.Mui-selected:hover': { backgroundColor: theme.palette.neutral[100] },
        fontSize: 16
    };

    return (
        <>
            {!hidden && (
                <>
                    {label && <InputLabel id="demo-multiple-item-label">{label}</InputLabel>}
                    <Select
                        labelId="demo-multiple-item-label"
                        id="demo-multiple-item"
                        size={size}
                        displayEmpty
                        multiple={multiple}
                        value={selectedData}
                        onChange={handleChange}
                        title={t('TEXT_SELECT')}
                        renderValue={
                            renderValue ||
                            ((sel) => {
                                const getLabel = (val: string) => options?.find((o) => o.value === val)?.label || val;

                                // preText handling (single value expected)
                                if (preText && typeof sel === 'string') {
                                    const display = sel === '' ? placeholder || t('TEXT_SELECT') : getLabel(sel);
                                    return sel === '' ? preText + ' ' + display : `${preText} ${display}`;
                                }

                                const isEmpty =
                                    typeof sel === 'string' ? sel === '' : Array.isArray(sel) ? sel.length === 0 : !sel;
                                if (isEmpty) {
                                    return (
                                        <em style={{ color: theme.palette.semantic.int.link, ...placeholderStyle }}>
                                            {placeholder || t('TEXT_SELECT')}
                                        </em>
                                    );
                                }

                                // multi-select: map values to labels
                                if (Array.isArray(sel)) {
                                    const labels = sel.map((v) => getLabel(v));
                                    return labels.join(', ');
                                }

                                // single select: show the option label when available
                                return getLabel(sel as string);
                            })
                        }
                        disabled={disabled}
                        inputProps={{ 'aria-label': 'Without label', onBlur }}
                        sx={{ '& .MuiSelect-select': { fontWeight: 400, color: theme.palette.semantic.text.default }, ...sx }}
                        input={
                            <OutlinedInput
                                startAdornment={
                                    showTooltipError && error ? (
                                        <InputAdornment position="start">
                                            <FieldErrorTooltip open={openTooltipError} message={errorText} />
                                        </InputAdornment>
                                    ) : undefined
                                }
                            />
                        }
                        fullWidth
                        placeholder={placeholder}
                        error={error}
                        MenuProps={{
                            disableScrollLock: true,
                            ...MenuProps,
                            PaperProps: {
                                ...MenuProps?.PaperProps,
                                sx: {
                                    marginTop: 0,
                                    borderRadius: '4px',
                                    background: theme.palette.semantic.bg.default,
                                    fontWeight: '400 !important',

                                    /* Drop shadow/Small */
                                    boxShadow: `2px 4px 16px 0 ${theme.palette.semantic.text.brand}!important`
                                }
                            }
                        }}
                        {...(icon && { IconComponent: icon })}
                    >
                        {options?.length > 0 &&
                            options?.map((option, index) => {
                                const { value } = option;
                                return (
                                    <MenuItem
                                        key={value}
                                        value={value}
                                        sx={menuItemSx}
                                        style={listItemStyle}
                                    >
                                        {renderMenuItem ? renderMenuItem(option, index) : option.label}
                                    </MenuItem>
                                );
                            })}
                    </Select>
                </>
            )}
        </>
    );
};
